import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle } from "lucide-react";
import { BrandMark, BrandName } from "@/components/BrandMark";
import { api } from "@/lib/api";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ink min-h-screen text-cream flex items-center justify-center px-4" data-testid="admin-reset-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6" data-testid="reset-logo">
            <BrandMark size={40} iconSize={18} />
            <BrandName className="font-display text-2xl text-cream" />
          </Link>
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold">Set New Password</p>
        </div>

        {done ? (
          <div className="rounded-3xl border border-gold/20 bg-card/70 p-10 text-center" data-testid="reset-success">
            <CheckCircle size={40} className="text-emerald-400 mx-auto mb-4" />
            <p className="text-cream font-semibold mb-1">Password updated</p>
            <p className="text-sm text-faded">Redirecting you to sign in…</p>
          </div>
        ) : !token ? (
          <div className="rounded-3xl border border-gold/20 bg-card/70 p-10 text-center" data-testid="reset-no-token">
            <p className="text-sm text-faded">This reset link is missing its token. Please use the full link from your email.</p>
            <Link to="/admin/login" className="text-xs text-gold hover:underline mt-4 inline-block">← Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="admin-reset-form" className="rounded-3xl border border-gold/20 bg-card/70 p-8 sm:p-10 space-y-5">
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">New Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                data-testid="reset-password-input"
                className="field-input"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Confirm Password</label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                className="field-input"
                placeholder="Repeat the password"
              />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-gold text-ink font-semibold text-sm px-8 py-4 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
            >
              <Lock size={15} />
              {loading ? "Updating…" : "Update Password"}
            </button>
            <p className="text-center">
              <Link to="/admin/login" className="text-xs text-faded hover:text-gold transition-colors" data-testid="reset-back-login">
                ← Back to sign in
              </Link>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
